import api from '../api.js';
import db from '../../versa/db.js';
import crud from '../../versa/crud.js';
import filter, { translate, translateForLocations, resolveFilterLabelsToIds } from '../../versa/filter.js';
import router from '../../versa/router.js';
import formset from '../../versa/formset.js';
import record, { flatten, deepen, getTopIndex } from '../../versa/record.js';
import dialog from '../../versa/dialog.js';
import { notify, ask } from '../../versa/msgbox.js';
import { splitIdsByCompany, resolvePrinterDeviceId } from '../../versa/misc.js';
import pdf from '../../versa/pdf.js';
import view from '../views/outputs.js';
import form from '../forms/outputs.js';
import buildCreateInvoicesForm, { buildOutputPurposeInnerHtml } from '../forms/create-invoices.js';
import import_picklist_form from '../forms/outputs-import-picklist.js';
import print_labels_form from '../forms/outputs-labels.js';
import print_stage_labels_form from '../forms/outputs-stage-labels.js';
import export_csv_form from '../forms/outputs-export-csv.js';
import export_items_csv_form from '../forms/outputs-export-items-csv.js';
import outputs_results_view from '../views/outputs-results.js';
import reverse_form from '../forms/outputs-reverse.js';
import openConferenceModal from '../forms/outputs-conference.js';
import openOutputDetailModal from '../forms/outputs-detail.js';
import { openShortageModal, hasShortage } from '../forms/outputs-shortage.js';
import { openUnpickRequestsModal } from '../forms/outputs-unpick-requests.js';
import { openPickingBoxesForOutputModal } from '../utils/output-picking-box-ops.js';
import openOutputPickingModal from '../forms/outputs-picking.js';
import * as types from '../types.js';
import { refreshIntegrationEntityAlerts } from '../utils/integration-entity-alerts.js';
import { referenceSearchPayloadFromQuery } from '../utils/bulk-reference-search.js';
import { consumeOutputsPendingFilter } from '../utils/navigate-to-outputs.js';

/** Status: Criado (SESSP), Separando (layout padrão). */
const OUTPUT_ST_CREATED = 10;
const OUTPUT_ST_PICKING = 20;
const OUTPUT_ST_CHECKED = 60;
const OUTPUT_ST_CANCELED = 100;
const OUTPUT_ST_DISABLED = 999;

const VOLUME_LABEL_PRE_CONFERENCE_MSG =
	'Alguma saída selecionada ainda não foi conferida.\n\n'
	+ 'Imprimir etiquetas de volumes antes da conferência pode gerar divergência entre '
	+ 'a quantidade de volumes etiquetados e a quantidade conferida na expedição.\n\n'
	+ 'Deseja continuar mesmo assim?';

/** Modal: escolhe layout padrão (QR JSON) ou SESSP (QR nNF|vol|total). */
function pickVolumeLabelLayout(onPick)
{
	const html = `
		<div class="msgbox">
			<div class="mb-title">Layout da etiqueta</div>
			<div class="mb-text">
				<p style="margin-bottom:0.75rem">Escolha o modelo de impressão.</p>
				<label class="volume-label-layout-opt" style="display:flex;align-items:flex-start;gap:0.5rem;margin:0.5rem 0;cursor:pointer">
					<input type="radio" name="volume_label_layout" value="default" checked style="margin-top:0.2rem">
					<span><strong>Padrão</strong> — mesmo layout visual de sempre; QR em JSON (empresa, saída e volume).</span>
				</label>
				<label class="volume-label-layout-opt" style="display:flex;align-items:flex-start;gap:0.5rem;margin:0.5rem 0;cursor:pointer">
					<input type="radio" name="volume_label_layout" value="sessp" style="margin-top:0.2rem">
					<span><strong>Etiqueta expedição</strong> — logo do cliente, destinatário, rota e QR <code>fatura|volume|total</code>. Fatura = NF-e ou trecho após <code>|</code> na Referência (ex.: <code>1420138|3010307</code>). Disponível a partir de <strong>Criado</strong>.</span>
				</label>
			</div>
			<button type="button" class="button mtl bn_N">Cancelar</button>
			<button type="button" class="button mtl bn_Y">Continuar</button>
		</div>
	`;
	const dlg = dialog.create(html, (key) =>
	{
		if (key === 'Escape' || key === 'N')
		{
			dlg.destroy();
			return;
		}
		if (key === 'Y')
		{
			const r = dlg.getNode().querySelector('input[name="volume_label_layout"]:checked');
			const layout = r && r.value ? r.value : 'default';
			dlg.destroy();
			onPick(layout);
		}
	}, /Enter|Escape|N|Y/);
}

/** Saídas elegíveis para etiqueta de volumes entre as linhas da grade. */
function selectedOutputsForVolumeLabels(crud, ids, rows)
{
	const idSet = new Set(ids.map(([c, id]) => `${c}:${id}`));
	return (rows || []).filter((r) => idSet.has(`${r.company_id}:${r.id}`));
}

function outputAllowsVolumeLabels(status, layout)
{
	const st = Number(status);
	if (st === OUTPUT_ST_CANCELED || st === OUTPUT_ST_DISABLED)
		return false;
	const min = layout === 'sessp' ? OUTPUT_ST_CREATED : OUTPUT_ST_PICKING;
	return st >= min;
}

function outputVolumeLabelsPreConference(status, layout)
{
	const st = Number(status);
	const min = layout === 'sessp' ? OUTPUT_ST_CREATED : OUTPUT_ST_PICKING;
	return st >= min && st < OUTPUT_ST_CHECKED;
}

function downloadPalletsExport(base64Content, filename, mimeType)
{
	const bin = atob(base64Content);
	const arr = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
	const blob = new Blob([arr], { type: mimeType });
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = filename || 'download';
	a.click();
	URL.revokeObjectURL(url);
}

/** Modal: filtros opcionais (período no extrato, estornos) para rastreio de saldo. */
function pickBalanceTraceFilters(selectedCount, onPick)
{
	const html = `
		<div class="msgbox">
			<div class="mb-title">Rastreio de saldo</div>
			<div class="mb-text">
				<p style="margin-bottom:0.75rem"><strong>${selectedCount}</strong> saída(ões) selecionada(s).</p>
				<p style="margin-bottom:0.5rem;font-size:0.9rem">Filtrar por <strong>data da movimentação</strong> no extrato (opcional):</p>
				<div style="display:flex;gap:1rem;flex-wrap:wrap;margin-bottom:0.75rem">
					<label>De <input type="date" id="bt-date-from" autocomplete="off"></label>
					<label>Até <input type="date" id="bt-date-to" autocomplete="off"></label>
				</div>
				<label style="display:flex;align-items:flex-start;gap:0.5rem;cursor:pointer;margin-top:0.5rem">
					<input type="checkbox" id="bt-include-estornos" checked style="margin-top:0.2rem">
					<span>Incluir movimentos <strong>estornados</strong> (linhas desativadas no extrato e a inversa do estorno)</span>
				</label>
				<p style="margin-top:0.75rem;margin-bottom:0.35rem;font-size:0.9rem">Formato:</p>
				<label style="display:flex;align-items:flex-start;gap:0.5rem;margin:0.35rem 0;cursor:pointer">
					<input type="radio" name="bt-format" value="pdf" checked style="margin-top:0.2rem">
					<span><strong>PDF</strong> — visualização</span>
				</label>
				<label style="display:flex;align-items:flex-start;gap:0.5rem;margin:0.35rem 0;cursor:pointer">
					<input type="radio" name="bt-format" value="csv" style="margin-top:0.2rem">
					<span><strong>CSV</strong> — separador <code>;</code> (UTF-8), colunas empresa, CNPJ, pedido, transação, origem/destino, etc.</span>
				</label>
				<p style="margin-top:0.75rem;font-size:0.85rem;color:#444">O relatório lista reservas (L→R), transferências entre posições (L→L, ex.: SS-01), baixa (R→O) e demais vínculos ao <code>output_id</code> no JSON da transação.</p>
			</div>
			<button type="button" class="button mtl bn_N">Cancelar</button>
			<button type="button" class="button mtl bn_Y">Gerar</button>
		</div>
	`;
	const dlg = dialog.create(html, (key) =>
	{
		if (key === 'Escape' || key === 'N')
		{
			dlg.destroy();
			return;
		}
		if (key === 'Y')
		{
			const node = dlg.getNode();
			const df = node.querySelector('#bt-date-from')?.value?.trim() || '';
			const dt = node.querySelector('#bt-date-to')?.value?.trim() || '';
			const incl = node.querySelector('#bt-include-estornos')?.checked !== false;
			const rf = node.querySelector('input[name="bt-format"]:checked');
			const format = rf && rf.value ? rf.value : 'pdf';
			dlg.destroy();
			onPick({
				date_from: df || undefined,
				date_to: dt || undefined,
				include_estornos: incl,
				format,
			});
		}
	}, /Enter|Escape|N|Y/);
}

/** Modal: PDF em grade (atual) ou tabela CSV/Excel com filtro de data opcional. */
function pickPalletsOverturnFormat(count, onPick)
{
	const html = `
		<div class="msgbox">
			<div class="mb-title">Derrubada de Pallets</div>
			<div class="mb-text">
				<p style="margin-bottom:0.75rem"><strong>${count}</strong> saída(ões) selecionada(s).</p>
				<p style="margin-bottom:0.5rem">Formato do relatório:</p>
				<label style="display:flex;align-items:flex-start;gap:0.5rem;margin:0.5rem 0;cursor:pointer">
					<input type="radio" name="po_layout" value="grade" checked style="margin-top:0.2rem">
					<span><strong>GRADE — PDF</strong> — grade de posições fora de alcance (comportamento atual).</span>
				</label>
				<label style="display:flex;align-items:flex-start;gap:0.5rem;margin:0.5rem 0;cursor:pointer">
					<input type="radio" name="po_layout" value="tabela" style="margin-top:0.2rem">
					<span><strong>Tabela — CSV ou Excel</strong> — linhas por item (posição, categoria térmica/SKU/produto/lote, fatura, quantidade).</span>
				</label>
				<div id="pallets-overturn-table-opts" style="display:none;margin-top:0.75rem;padding-top:0.75rem;border-top:1px solid #ddd">
					<p style="margin-bottom:0.5rem;font-size:0.9rem"><strong>Filtro de data</strong> (opcional — data de criação da saída; deixe em branco para todas as selecionadas)</p>
					<div style="display:flex;gap:1rem;flex-wrap:wrap;margin-bottom:0.75rem">
						<label>De <input type="date" id="po-date-from" autocomplete="off"></label>
						<label>Até <input type="date" id="po-date-to" autocomplete="off"></label>
					</div>
					<p style="margin-bottom:0.35rem">Arquivo:</p>
					<label style="margin-right:0.75rem;cursor:pointer"><input type="radio" name="po_export_fmt" value="csv" checked> CSV</label>
					<label style="cursor:pointer"><input type="radio" name="po_export_fmt" value="xlsx"> Excel (.xlsx)</label>
				</div>
			</div>
			<button type="button" class="button mtl bn_N">Cancelar</button>
			<button type="button" class="button mtl bn_Y">Gerar</button>
		</div>
	`;
	const dlg = dialog.create(html, (key) =>
	{
		if (key === 'Escape' || key === 'N')
		{
			dlg.destroy();
			return;
		}
		if (key === 'Y')
		{
			const node = dlg.getNode();
			const lr = node.querySelector('input[name="po_layout"]:checked');
			const layout = lr && lr.value ? lr.value : 'grade';
			let dateFrom = '';
			let dateTo = '';
			let format = 'csv';
			if (layout === 'tabela')
			{
				dateFrom = (node.querySelector('#po-date-from') || {}).value || '';
				dateTo = (node.querySelector('#po-date-to') || {}).value || '';
				if (dateFrom && dateTo && dateFrom > dateTo)
				{
					notify('Data inicial não pode ser maior que a final.', 'Filtro de data');
					return;
				}
				const rf = node.querySelector('input[name="po_export_fmt"]:checked');
				format = rf && rf.value ? rf.value : 'csv';
			}
			dlg.destroy();
			onPick({ layout, format, date_from: dateFrom || undefined, date_to: dateTo || undefined });
		}
	}, /Enter|Escape|N|Y/);
	const node = dlg.getNode();
	const box = node.querySelector('#pallets-overturn-table-opts');
	const syncOpts = () =>
	{
		const r = node.querySelector('input[name="po_layout"]:checked');
		const layout = r && r.value ? r.value : 'grade';
		if (box) box.style.display = layout === 'tabela' ? 'block' : 'none';
	};
	node.querySelectorAll('input[name="po_layout"]').forEach((el) => el.addEventListener('change', syncOpts));
	syncOpts();
}

const outputs = function()
{

	let outputs_crud;

	function bindOutputStatusFieldLock(fs)
	{
		const hint = fs.dialog?.getNode?.()?.querySelector('#output-status-lock-hint');
		const statusField = fs.form.getFieldBySelector('#status');
		const allowRaw = fs.form.getFieldBySelector('#allow_manual_status_advance')?.getValue?.();
		const allow = allowRaw !== false && allowRaw !== 'false' && allowRaw !== '0' && allowRaw !== 0;
		const stRaw = statusField?.getValue?.();
		const st = stRaw != null && stRaw !== '' ? Number(stRaw) : NaN;
		const lock = !allow && Number.isFinite(st) && st >= 30 && st < 65;
		const statusNode = statusField?.queryNode?.() || statusField?.node;
		if (statusNode)
		{
			if (lock)
			{
				statusNode.setAttribute('disabled', 'disabled');
				statusNode.setAttribute('title', 'Use Operações → Conferir saída (conferência web)');
			}
			else
			{
				statusNode.removeAttribute('disabled');
				statusNode.removeAttribute('title');
			}
		}
		if (hint)
			hint.style.display = lock ? 'block' : 'none';
	}

	function bindDeleteSeparateLinesUI(fs)
	{
		const root = fs.dialog.getNode();
		const btn = root.querySelector('#bn-delete-separate-output-lines');
		const statusEl = root.querySelector('#status');
		if (!btn)
			return;
		const syncVisibility = () =>
		{
			const raw = fs.form.getFieldBySelector('#status')?.getValue?.();
			const st = raw != null && raw !== '' ? Number(raw) : NaN;
			const ok = Number.isFinite(st) && st >= 10 && st < 70;
			btn.style.display = ok ? 'inline-block' : 'none';
			btn.disabled = !ok;
		};
		syncVisibility();
		if (statusEl)
			statusEl.addEventListener('change', syncVisibility);
		btn.onclick = async () =>
		{
			const indices = fs.getSelectedIndices('.row');
			if (!indices.length)
			{
				notify('Marque as linhas a remover (checkbox à esquerda).', 'Saídas');
				return;
			}
			const company_id = fs.form.getFieldBySelector('#company_id').getValue();
			const output_id = Number(fs.form.getFieldBySelector('#id').getValue());
			if (!company_id || !output_id)
			{
				notify('Saída ou empresa inválida.', 'Saídas');
				return;
			}
			const nums = [];
			for (const idx of indices)
			{
				const el = root.querySelector(`#number-${idx}`);
				const n = el && el.value;
				if (n != null && String(n).trim() !== '' && Number.isFinite(Number(n)))
					nums.push(Number(n));
			}
			if (!nums.length)
			{
				notify('Não foi possível ler o número das linhas (feche e abra o registro novamente).', 'Saídas');
				return;
			}
			ask(
				`Remover ${nums.length} linha(s) no servidor (${nums.join(', ')})? Se houver reserva, ela volta ao estoque livre (R→L).`,
				'Confirmar remoção',
				async (answer) =>
				{
					if (answer !== 'Y')
						return;
					const resp = await api.call(api.delete_output_separate_lines, company_id, output_id, { item_numbers: nums });
					if (!resp)
						return;
					notify(`Linhas removidas: ${nums.join(', ')}.`, 'Saídas');
					const flat = await outputs_crud.loadRecord(JSON.stringify([company_id, output_id]));
					const top = getTopIndex(flat);
					let have = fs.getTableSize();
					while (have > top + 1)
					{
						fs.removeRows([have - 1], '.row');
						have--;
					}
					while (have < top + 1)
					{
						fs.appendRows(1, '.row');
						have++;
					}
					fs.form.setValues(flat);
					fs.form.refresh();
					setTimeout(() => fs.form.refresh(), 200);
					syncVisibility();
				}
			);
		};
	}

	const record_output_form = {
		...form,
		form: {
			...form.form,
			afterEditSetValues(fs)
			{
				if (fs.hasTables?.())
					fs.setTableHeight(10);
				bindOutputStatusFieldLock(fs);
				bindDeleteSeparateLinesUI(fs);
				// Dispara observers de remetente/armazém para popular o datalist de posição (IDB) após loadRecord,
				// sem depender de clique do usuário — evita valor cru "wh:loc" sem rótulo de endereço.
				const rem = fs.form.getFieldBySelector('#warehouse_company_id');
				const wh = fs.form.getFieldBySelector('#warehouse_id');
				const remVal = rem?.getValue?.();
				if (rem && typeof rem.publish === 'function' && remVal != null && remVal !== '')
					rem.publish('change', rem, remVal);
				const whVal = wh?.getValue?.();
				if (wh && typeof wh.publish === 'function' && whVal != null && whVal !== '')
					wh.publish('change', wh, whVal);
				setTimeout(() => fs.form.refresh(), 250);
				const vals = fs.form.getValues?.() || {};
				const compId = vals.company_id;
				const orderId = vals.order_id;
				const orderType = String(vals.order_type || '').trim().toUpperCase();
				if (compId && orderId != null && orderId !== '' && orderType === 'O')
				{
					refreshIntegrationEntityAlerts({
						formSelector: '#output',
						bannerId: 'output-integration-alert-banner',
						companyId: compId,
						entityType: 'output_order',
						entityId: orderId,
					});
				}
			},
		},
	};

	outputs_crud = crud.create({
		/** Posições no IndexedDB por empresa do remetente (todas as filiais), para rótulo `endereço · arm. N` em linhas multi-armazém. */
		async queryLocationsWideForIndexedDb()
		{
			const params = new URLSearchParams();
			let f = this.filter;
			if (f && Object.keys(f).some(k => /^field-\d+$/.test(k)))
				f = await resolveFilterLabelsToIds(f, this.fieldspecs);
			let expr = translate(f, ['company_id'], this.fieldspecs);
			if (!expr)
				expr = translateForLocations(f, this.fieldspecs);
			// Sem filtro de escopo: GET /locations sem WHERE puxava todo o cadastro → lento e inútil para o datalist.
			// O modal de saída hidrata posições via loadRecord (empresa do remetente) e filtros explícitos recarregam aqui.
			if (!expr)
				return null;
			params.append('filter', expr);
			params.append('sort', '4');
			params.append('limit', '0');
			return '?' + params.toString();
		},
		async loadBaseData()
		{
			api.call(api.list_companies, this.queryAll()).then((data) => db.set('companies', data).catch(error => notify(error)));
			api.call(api.list_customers, this.queryAll()).then((data) => db.set('customers', data).catch(() => {}));
			api.call(api.list_customer_routes, this.queryAll()).then((data) => db.set('customer_routes', data).catch(() => {}));
			api.call(api.list_carriers, this.queryAll()).then((data) => db.set('carriers', data).catch(() => {}));
			api.call(api.list_programs, this.queryAll()).then((data) => db.set('programs', data).catch(() => {}));
			api.call(api.list_warehouses, this.queryAll()).then((data) => db.set('warehouses', data).catch(error => notify(error)));
			const locQ = await this.queryLocationsWideForIndexedDb();
			if (locQ)
				api.call(api.list_locations, locQ).then((data) => db.set('locations', data).catch(error => notify(error)));
			api.call(api.list_devices, this.queryAll()).then((data) => db.set('devices', data).catch(error => notify(error)));
		},
		async queryData(query)
		{
			const searchPayload = referenceSearchPayloadFromQuery(this.filter, query);
			if (searchPayload)
				return await api.call(api.search_outputs, searchPayload);
			return await api.call(api.list_outputs, query);
		},
		/**
		 * Listagem filtrada sem paginação (ações em massa / etiquetas).
		 * Usa lista leve + teto — evita GET /outputs?limit=0 com itens aninhados (uWSGI).
		 */
		async queryFiltered()
		{
			const params = new URLSearchParams();
			await this.addFilter(params);
			this.addSort(params);
			this.addLimit(params, 500);
			params.set('include_items', '0');
			return '?' + params.toString();
		},
		/**
		 * Carrega produtos da empresa no IndexedDB para o datalist do modal de itens.
		 * Mesmo padrão de Entradas (`loadCompanyProductsToDb`) — Saídas não fazia isso
		 * e no QA (IDB vazio) o campo mostrava só o ID + «Verificar product».
		 */
		async loadCompanyProductsToDb(company_id)
		{
			if (company_id == null || company_id === '')
				return;
			const qs = new URLSearchParams({
				filter: `company_id*eq*${company_id}`,
				sort: '2',
				limit: '0',
			});
			const data = await api.call(api.list_products, '?' + qs.toString());
			if (Array.isArray(data))
				await db.set('products', data);
		},
		/**
		 * Lotes da empresa para datalist (observer product_id + company_id).
		 * Escopo por empresa — evita depender de o usuário ter aberto Cadastro → Lotes.
		 */
		async loadCompanyLotsToDb(company_id)
		{
			if (company_id == null || company_id === '')
				return;
			const qs = new URLSearchParams({
				filter: `company_id*eq*${company_id}`,
				limit: '0',
			});
			const data = await api.call(api.list_lots, '?' + qs.toString());
			if (Array.isArray(data))
				await db.set('lots', data);
		},
		async loadRecord(id)
		{
			let _record = await api.call(api.select_output, ...JSON.parse(id));
			const flat = flatten(_record, 'items');
			const hdrWh = _record.warehouse_id;
			const top = getTopIndex(flat);
			for (let i = 0; i <= top; i++)
			{
				const lid = flat[`location_id-${i}`];
				const wline = flat[`warehouse_id-${i}`] ?? hdrWh;
				if (lid != null && lid !== '' && wline != null && wline !== '' && !String(lid).includes(':'))
					flat[`location_id-${i}`] = `${Number(wline)}:${Number(lid)}`;
			}
			try
			{
				await this.loadCompanyProductsToDb(_record.company_id);
			}
			catch (_) { /* datalist de produtos pode ficar vazio; modal ainda abre */ }
			try
			{
				await this.loadCompanyLotsToDb(_record.company_id);
			}
			catch (_) { /* datalist de lotes pode ficar vazio */ }
			const rem = _record.warehouse_company_id;
			if (rem)
			{
				const qs = new URLSearchParams({
					filter: `company_id*eq*${rem}`,
					sort: '4',
					limit: '0',
				}).toString();
				try
				{
					const locs = await api.call(api.list_locations, '?' + qs);
					if (Array.isArray(locs) && locs.length)
						await db.set('locations', locs);
				}
				catch (_) { /* offline / permissão: datalist pode ficar só com o que já havia no IDB */ }
			}
			flat.allow_manual_status_advance = _record.allow_manual_status_advance !== false;
			return flat;
		},
		editRecord(data)
		{
			record.edit(record_output_form, this.saveRecordWrapper.bind(this), data, false);	// false = turn off autorow for this module
		},
		async editFilter() {
			const locQf = await this.queryLocationsWideForIndexedDb();
			if (locQf)
				api.call(api.list_locations, locQf).then((data) => db.set('locations', data)).catch(() => {});
			filter.edit(this.applyFilter.bind(this), this.filter, {
				reloadLocations: async (formData) => {
					const resolved = await resolveFilterLabelsToIds(formData, this.fieldspecs);
					const parts = [];
					for (let i = 0; i < 10; i++) {
						const f = resolved[`field-${i}`];
						const v = resolved[`value-${i}`];
						if (f === 'company_id' && v) parts.push(`company_id*eq*${v}`);
						else if (f === 'warehouse_id' && v != null && v !== '') parts.push(`warehouse_id*eq*${v}`);
						else if (f === 'warehouse_company_id' && v) parts.push(`company_id*eq*${v}`);
					}
					if (parts.length === 0) return;
					const params = new URLSearchParams();
					params.append('filter', parts.join(','));
					params.append('sort', '4');
					params.append('limit', '0');
					const data = await api.call(api.list_locations, '?' + params.toString());
					if (data && Array.isArray(data)) db.set('locations', data);
				}
			});
		},
		async applyFilter(data) {
			this.filter = await resolveFilterLabelsToIds(data, this.fieldspecs);
			this.filterExplicitlyApplied = true;
			const locQf = await this.queryLocationsWideForIndexedDb();
			if (locQf)
				api.call(api.list_locations, locQf).then((data) => db.set('locations', data)).catch(() => {});
			await this.loadPage(1);
		},
		/**
		 * Abertura com filtro vindo de outra tela (ex.: Dose Certa → link da saída).
		 */
		async run(module_name, label, target_node, grid_view, record_form, fieldspecs, operations, reports, options = {}) {
			const pending = consumeOutputsPendingFilter();
			await Object.getPrototypeOf(this).run.call(
				this, module_name, label, target_node, grid_view, record_form, fieldspecs, operations, reports,
				{ ...options, skipInitialLoad: Boolean(pending) || Boolean(options.skipInitialLoad) },
			);
			if (!pending)
				return;
			this.filter = pending;
			this.filterExplicitlyApplied = true;
			try {
				this.persistKeepFilterSnapshot?.();
			} catch { /* ignore */ }
			const locQf = await this.queryLocationsWideForIndexedDb();
			if (locQf)
				api.call(api.list_locations, locQf).then((data) => db.set('locations', data)).catch(() => {});
			await this.loadPage(1);
		},
		async saveRecord(data, old_data)
		{
			data = deepen(data, 'items');
			if (!data.items)	// Invalid order number used
				return;
			let _record = {
				order_type: data.order_type,
				order_id: Number(data.order_id),
				warehouse_company_id: data.warehouse_company_id,
				warehouse_id: Number(data.warehouse_id),
				volume_type: data.volume_type ?? null,
				volumes: Number(data.volumes),
				weight: Number(data.weight),
				status: Number(data.status),
				items: data.items
				.filter(item => item && (item.product_id != null && item.product_id !== '') && Number(item.product_id) > 0)
				.map(item =>
				{
					let locRaw = item.location_id;
					let whLine = item.warehouse_id;
					const locStr = String(locRaw ?? '').trim();
					if (/^\d+:\d+$/.test(locStr))
					{
						const [a, b] = locStr.split(':').map(Number);
						whLine = whLine ?? a;
						locRaw = b;
					}
					if (whLine == null || whLine === '')
						whLine = data.warehouse_id;
					const numRaw = item.number;
					const out = {
						location_id: Number(locRaw),
						warehouse_id: whLine != null && whLine !== '' ? Number(whLine) : null,
						product_id: Number(item.product_id),
						lot_id: item.lot_id > 0 ? Number(item.lot_id) : null,
						quantity_required: Number(item.quantity_required),
						quantity_supplied: Number(item.quantity_supplied),
						discount: Number(item.discount),
						unit_cost: 0,	// These numbers will be recalculated at the backend
						unit_price: 0
					};
					if (numRaw != null && String(numRaw).trim() !== '' && Number.isFinite(Number(numRaw)))
						out.number = Number(numRaw);
					return out;
				})
			};
			if (old_data)
				return await api.call(api.update_output, data.company_id, data.id, _record);
			else
				return await api.call(api.insert_output, data.company_id, _record);
		},
		cancelOutputs()
		{
			if (!this.ensureSelection('Cancelar Saídas'))
				return;
			const showResult = async (results) =>
			{
				notify(await outputs_results_view(results), 'Resultados');
				this.loadPage();
			}
			const sendRequest = async () =>
			{
				let ids = await this.getSelectedIds(['company_id', 'id']);
				const resp = await api.call(api.cancel_outputs, { 'ids': ids });
				if (resp != null)
					await showResult(resp);
			}
			ask(`Cancelar reservas de ${this.getCount()} saídas?`, 'Cancelar Saídas', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		async createInvoices()
		{
			if (!this.ensureSelection('Gerar Notas'))
				return;
			const showResult = (resp) =>
			{
				this.clearGridSelection();
				notify(`Notas criadas: ${resp?.count || 0}`, 'Resultado');
				fs.destroy();
			};
			const sendRequest = async (data) =>
			{
				const ids = await this.getSelectedIds(['company_id', 'id']);
				const purposeRaw = String(data.purpose ?? '').trim();
				const payload = {
					io_type: 'O',
					ids,
					info: data.info,
					include: data.include,
				};
				if (/^[1-6]$/.test(purposeRaw))
					payload.purpose = purposeRaw;
				else if (purposeRaw)
					payload.cfop = purposeRaw;
				else
					throw new Error('Selecione uma finalidade ou um CFOP cadastrado.');
				const resp = await api.call(api.create_invoices, payload);
				if (resp != null)
					showResult(resp);
			};
			let formDef;
			try
			{
				const ids = await this.getSelectedIds(['company_id', 'id']);
				const companies = new Set(ids.map((r) => r[0]));
				if (companies.size === 1)
				{
					const companyId = [...companies][0];
					const rows = await api.call(api.list_cfops_by_company, companyId);
					formDef = buildCreateInvoicesForm(false, buildOutputPurposeInnerHtml(rows));
				}
				else
					formDef = buildCreateInvoicesForm(false);
			}
			catch (e)
			{
				notify(String(e.message || e), 'Erro');
				return;
			}
			let fs = formset.create(formDef, sendRequest);
			fs.init = (() =>
			{
				const n = this.getCount();
				document.querySelector(`${fs.form.selector} #count`).innerHTML = n;
				const noun = document.querySelector(`${fs.form.selector} #io-noun`);
				if (noun)
					noun.textContent = n === 1 ? 'saída' : 'saídas';
				Object.getPrototypeOf(fs).init.call(fs);
			})();
		},
		closeOutputs()
		{
			if (!this.ensureSelection('Baixar Estoques'))
				return;
			const showResult = async (results) =>
			{
				notify(await outputs_results_view(results), 'Resultados');
				this.loadPage();
			}
			const sendRequest = async () =>
			{
				let ids = await this.getSelectedIds(['company_id', 'id']);
				const resp = await api.call(api.close_outputs, { 'ids': ids });
				if (resp != null)
					await showResult(resp);
			}
			ask(`Dar baixa de ${this.getCount()} saídas?`, 'Baixar Estoques', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		/** #MT-0F84944 — multi-select → Doca Expedição (status 70 → 75, sem baixa). */
		sendToDockExpedition()
		{
			if (!this.ensureSelection('Enviar para Doca Expedição'))
				return;
			const showResult = async (resp) =>
			{
				const sent = resp?.count ?? 0;
				const already = (resp?.already || []).length;
				const errs = resp?.errors || [];
				let msg = `Enviadas à Doca: ${sent}`;
				if (already)
					msg += ` · Já estavam na Doca: ${already}`;
				if (errs.length)
					msg += `\nFalhas (${errs.length}): ` + errs.slice(0, 8).map((e) =>
						`saída ${e.output_id}: ${e.error}`
					).join('; ');
				notify(msg, errs.length ? 'Atenção' : 'Doca Expedição');
				this.loadPage();
			};
			const sendRequest = async () =>
			{
				const ids = await this.getSelectedIds(['company_id', 'id']);
				const resp = await api.call(api.send_outputs_to_dock_expedition, { ids });
				if (resp != null)
					await showResult(resp);
			};
			ask(
				`Enviar ${this.getCount()} saída(s) para Doca Expedição?\n` +
				`Somente Faturadas, com Doca Expedição ativa na empresa. Sem «Todos os canais na Doca», só o canal Internet.`,
				'Enviar para Doca Expedição',
				(answer) =>
				{
					if (answer == 'Y')
						sendRequest();
				}
			);
		},
		async exportListCsv()
		{
			const crud = this;
			await record.edit(export_csv_form, async () =>
			{
				const box = document.querySelector(`${export_csv_form.form.selector} #use_list_filter`);
				const useList = box ? box.checked : true;
				const payload = {};
				if (useList)
				{
					const params = new URLSearchParams();
					await crud.addFilter(params);
					crud.addSort(params);
					const f = params.get('filter');
					const s = params.get('sort');
					if (f)
						payload.filter = f;
					if (s)
						payload.sort = s;
				}
				try
				{
					const resp = await api.call(api.export_outputs_csv, payload);
					if (resp?.csv)
					{
						downloadPalletsExport(resp.csv, resp.filename || 'saidas.csv', 'text/csv;charset=utf-8');
						notify('Download iniciado.', 'Exportar listagem (CSV)');
					}
					else
						notify('Não foi possível gerar o arquivo.', 'Aviso');
				}
				catch (e)
				{
					notify(e?.message || String(e), 'Erro');
				}
			}, {}, false);
		},
		async exportItemsCsv()
		{
			const crud = this;
			await record.edit(export_items_csv_form, async () =>
			{
				const box = document.querySelector(`${export_items_csv_form.form.selector} #use_list_filter`);
				const useList = box ? box.checked : true;
				const payload = {};
				if (useList)
				{
					const params = new URLSearchParams();
					await crud.addFilter(params);
					crud.addSort(params);
					const f = params.get('filter');
					const s = params.get('sort');
					if (f)
						payload.filter = f;
					if (s)
						payload.sort = s;
				}
				try
				{
					const resp = await api.call(api.export_outputs_items_csv, payload);
					if (resp?.csv)
					{
						downloadPalletsExport(resp.csv, resp.filename || 'saidas-itens.csv', 'text/csv;charset=utf-8');
						notify('Download iniciado.', 'Exportar itens (CSV)');
					}
					else
						notify('Não foi possível gerar o arquivo.', 'Aviso');
				}
				catch (e)
				{
					notify(e?.message || String(e), 'Erro');
				}
			}, {}, false);
		},
		printPickControl()
		{
			if (!this.ensureSelection('Controle de Picking'))
				return;
			const showResult = async (resp) =>
			{
				this.clearGridSelection();
				if (resp?.pdf)
					pdf(resp.pdf);
			}
			const sendRequest = async () =>
			{
				let ids = splitIdsByCompany(await this.getSelectedIds(['company_id', 'id']));
				let resp = await api.call(api.print_pick_control, { 'ids': ids }).then(showResult);
			}
			ask(`Incluir ${this.getCount()} saídas no relatório?`, 'Controle de Picking', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		printPalletsOverturn()
		{
			if (!this.ensureSelection('Derrubada de Pallets'))
				return;
			const showResult = async (resp) =>
			{
				this.clearGridSelection();
				if (resp?.pdf)
					pdf(resp.pdf);
				else if (resp?.csv)
					downloadPalletsExport(resp.csv, resp.filename || 'derrubada-pallets.csv', 'text/csv;charset=utf-8');
				else if (resp?.xlsx)
					downloadPalletsExport(resp.xlsx, resp.filename || 'derrubada-pallets.xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
			};
			const sendRequest = async (opts) =>
			{
				let ids = splitIdsByCompany(await this.getSelectedIds(['company_id', 'id']));
				const body = { ids, layout: opts.layout || 'grade' };
				if (body.layout === 'tabela')
				{
					body.format = opts.format || 'csv';
					if (opts.date_from) body.date_from = opts.date_from;
					if (opts.date_to) body.date_to = opts.date_to;
				}
				await api.call(api.print_pallets_overturn, body).then(showResult);
			};
			pickPalletsOverturnFormat(this.getCount(), (opts) => { sendRequest.call(this, opts); });
		},
		printPickLists()
		{
			if (!this.ensureSelection('Pick List'))
				return;
			const showResult = async (resp) =>
			{
				this.clearGridSelection();
				if (resp?.pdf)
					pdf(resp.pdf);
				await this.loadPage();
			}
			const sendRequest = async () =>
			{
				let ids = await this.getSelectedIds(['company_id', 'id']);
				let resp = await api.call(api.print_picklists, { 'ids': ids }).then(showResult);
			}
			ask(`Incluir ${this.getCount()} saídas no relatório?`, 'Pick List', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		printPickListOrder()
		{
			if (!this.ensureSelection('PickList - Ordem'))
				return;
			const showResult = async (resp) =>
			{
				this.clearGridSelection();
				if (resp?.pdf)
					pdf(resp.pdf);
				await this.loadPage();
			};
			const sendRequest = async () =>
			{
				const ids = await this.getSelectedIds(['company_id', 'id']);
				await api.call(api.print_picklist_order, { ids }).then(showResult);
			};
			ask(`Incluir ${this.getCount()} saída(s) no PickList - Ordem?`, 'PickList - Ordem', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		printAvisoRemessa()
		{
			if (!this.ensureSelection('Aviso de Remessa'))
				return;
			const showResult = async (resp) =>
			{
				this.clearGridSelection();
				if (resp?.pdf)
					pdf(resp.pdf);
			}
			const sendRequest = async () =>
			{
				let ids = await this.getSelectedIds(['company_id', 'id']);
				let resp = await api.call(api.print_aviso_remessa, { 'ids': ids }).then(showResult);
			}
			ask(`Incluir ${this.getCount()} saídas no Aviso de Remessa?`, 'Aviso de Remessa', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		printConference()
		{
			if (!this.ensureSelection('Conferência de Saída'))
				return;
			const showResult = async (resp) =>
			{
				this.clearGridSelection();
				if (resp?.pdf)
					pdf(resp.pdf);
			}
			const sendRequest = async () =>
			{
				let ids = await this.getSelectedIds(['company_id', 'id']);
				let resp = await api.call(api.print_conference, { 'ids': ids }).then(showResult);
			}
			ask(`Incluir ${this.getCount()} saídas no relatório de conferência?`, 'Conferência de Saída', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		printDetails()
		{
			if (!this.ensureSelection('Saída Detalhada'))
				return;
			const showResult = async (resp) =>
			{
				this.clearGridSelection();
				if (resp?.pdf)
					pdf(resp.pdf);
				else if (resp?.message)
					notify(resp.message, 'Saída detalhada');
			};
			const sendRequest = async () =>
			{
				const ids = splitIdsByCompany(await this.getSelectedIds(['company_id', 'id']));
				const resp = await api.call(api.print_output_details, { ids }).then(showResult);
			};
			ask(`Incluir ${this.getCount()} saídas no relatório detalhado?`, 'Saída Detalhada', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		printBalanceTrace()
		{
			if (!this.ensureSelection('Rastreio de saldo'))
				return;
			const showResult = async (resp) =>
			{
				this.clearGridSelection();
				if (resp?.pdf)
					pdf(resp.pdf);
				else if (resp?.csv)
					downloadPalletsExport(resp.csv, resp.filename || 'rastreio-saldo.csv', 'text/csv;charset=utf-8');
			};
			const sendRequest = async (opts) =>
			{
				const ids = await this.getSelectedIds(['company_id', 'id']);
				const body = { ids };
				if (opts?.date_from)
					body.date_from = opts.date_from;
				if (opts?.date_to)
					body.date_to = opts.date_to;
				if (opts && typeof opts.include_estornos === 'boolean')
					body.include_estornos = opts.include_estornos;
				const fmt = opts?.format && String(opts.format).toLowerCase() === 'csv' ? 'csv' : 'pdf';
				body.format = fmt;
				await api.call(api.print_balance_trace, body).then(showResult);
			};
			pickBalanceTraceFilters(this.getCount(), (opts) => { sendRequest.call(this, opts); });
		},
		async printLabels()
		{
			if (!this.ensureSelection('Etiquetas de Volumes'))
				return;
			const sendRequest = async (data) =>
			{
				data = deepen(data, 'outputs');
				const device_id = resolvePrinterDeviceId(data.device_id, '#output-labels #device_id');
				if (device_id == null)
				{
					notify('Selecione a impressora na lista (clique na opção; só o texto no campo não basta).', 'Atenção');
					return false;
				}
				let layout = data.label_layout != null && String(data.label_layout).trim() !== ''
					? String(data.label_layout).trim().toLowerCase()
					: 'default';
				if (layout !== 'default' && layout !== 'sessp')
					layout = 'default';
				let _record = {
					device_company_id: data.device_company_id,
					device_id,
					return_zpl: !!data.use_local_agent,
					label_layout: layout,
					outputs: data.outputs.map(output => ({ 
						company_id: output.company_id,
						output_id: Number(output.output_id),
						first_volume: Number(output.first_volume),
						last_volume: Number(output.last_volume)
					}))
				};
				const result = await api.call(api.print_output_labels, _record);
				if (result?.zpl) {
					try {
						await api.sendZplToAgent(result.zpl, result.printer, result.printer_key);
						notify('Etiquetas enviadas ao agente local.', 'Impressão');
					} catch (e) {
						notify(e?.message || String(e), 'Agente local');
						return false;
					}
				}
				return result;
			}
			const editVolumes = async (data, labelLayout) =>
			{
				this.clearGridSelection();
				let _record = {
					orders: data,
					label_layout: labelLayout && String(labelLayout).trim() !== '' ? String(labelLayout).trim().toLowerCase() : 'default',
				};
				record.edit(print_labels_form, sendRequest, flatten(_record, 'orders'));
			}
			const getVolumes = async (labelLayout) =>
			{
				let ids = await this.getSelectedIds(['company_id', 'id']);
				const layout = labelLayout && String(labelLayout).trim() !== ''
					? String(labelLayout).trim().toLowerCase()
					: 'default';
				await api.call(api.list_output_volumes, { ids, label_layout: layout })
					.then((d) => editVolumes(d, layout));
			};
			const confirmPreConferenceIfNeeded = (selected, layout) =>
			{
				if (selected.some((r) => outputVolumeLabelsPreConference(r.status, layout)))
				{
					ask(VOLUME_LABEL_PRE_CONFERENCE_MSG, 'Etiquetas antes da conferência', (answer) =>
					{
						if (answer === 'Y')
							getVolumes(layout);
					});
					return;
				}
				getVolumes(layout);
			};
			ask(`Incluir ${this.getCount()} saídas na impressão?`, 'Etiquetas de Volumes', async (answer) =>
			{
				if (answer !== 'Y')
					return;
				const ids = await this.getSelectedIds(['company_id', 'id']);
				const rows = await this.queryFilteredData();
				const selected = selectedOutputsForVolumeLabels(this, ids, rows);
				pickVolumeLabelLayout((layout) =>
				{
					if (selected.some((r) => !outputAllowsVolumeLabels(r.status, layout)))
					{
						const msg = layout === 'sessp'
							? 'Etiqueta expedição disponível a partir do status Criado (não para saídas Cancelada ou Excluída).'
							: 'Etiquetas de volumes (layout Padrão) disponíveis a partir do status Separando (não para saídas Criada, Cancelada ou Excluída).';
						notify(msg, 'Etiquetas de Volumes');
						return;
					}
					confirmPreConferenceIfNeeded(selected, layout);
				});
			});
		},
		async printStageLabels()
		{
			if (!this.ensureSelection('Etiqueta de Saída'))
				return;
			const sendRequest = async (data) =>
			{
				data = deepen(data, 'outputs');
				const device_id = resolvePrinterDeviceId(data.device_id, '#output-stage-labels #device_id');
				if (device_id == null)
				{
					notify('Selecione a impressora na lista (clique na opção; só o texto no campo não basta).', 'Atenção');
					return false;
				}
				const outputs = (data.outputs || []).filter(
					(output) => output?.output_id != null && String(output.output_id).trim() !== '',
				);
				if (!outputs.length)
				{
					notify('Nenhuma saída válida para imprimir.', 'Etiqueta de Saída');
					return false;
				}
				const _record = {
					device_company_id: data.device_company_id,
					device_id,
					return_zpl: !!data.use_local_agent,
					outputs: outputs.map((output) => ({
						company_id: output.company_id,
						output_id: Number(output.output_id),
						label_count: Number(output.label_count) || 1,
					})),
				};
				const result = await api.call(api.print_output_stage_labels, _record);
				if (result?.zpl)
				{
					try
					{
						await api.sendZplToAgent(result.zpl, result.printer, result.printer_key);
						notify('Etiquetas enviadas ao agente local.', 'Impressão');
					}
					catch (e)
					{
						notify(e?.message || String(e), 'Agente local');
						return false;
					}
				}
				return result;
			};
			const editLabels = async (data) =>
			{
				this.clearGridSelection();
				record.edit(print_stage_labels_form, sendRequest, flatten({ outputs: data }, 'outputs'), false);
			};
			const getData = async () =>
			{
				const ids = await this.getSelectedIds(['company_id', 'id']);
				if (!ids.length)
				{
					notify('Selecione ao menos uma saída.', 'Etiqueta de Saída');
					return;
				}
				try
				{
					const data = await api.call(api.list_output_stage_labels, { ids });
					await editLabels(data);
				}
				catch (e)
				{
					notify(e?.message || String(e), 'Etiqueta de Saída');
				}
			};
			ask(`Incluir ${this.getCount()} saída(s) na impressão de Etiqueta de Saída?`, 'Etiqueta de Saída', async (answer) =>
			{
				if (answer !== 'Y')
					return;
				await getData();
			});
		},
		async reverseOutput()
		{
			const sendRequest = async (data) =>
			{
				let resp = await api.call(api.reverse_output, data.company_id, data.output_id, { status: Number(data.status) });
				if (resp)
					this.loadPage();	// Show updated status
				return resp;
			}
			let ids = await this.getSelectedIds(['company_id', 'id'])
			if (ids.length != 1)
				return notify('Selecione apenas uma saída para estornar', 'Estorno de Saída');
			record.edit(reverse_form, sendRequest, { company_id: ids[0][0], output_id: ids[0][1] });
		},
		async conferirSaida()
		{
			const ids = await this.getSelectedIds(['company_id', 'id']);
			if (ids.length !== 1)
				return notify('Selecione apenas uma saída para conferir.', 'Conferência de Saída');
			const [companyId, outputId] = ids[0];
			await openConferenceModal(companyId, outputId, () => this.loadPage());
		},
		async verDetalhesSaida()
		{
			const ids = await this.getSelectedIds(['company_id', 'id']);
			if (ids.length !== 1)
				return notify('Selecione apenas uma saída para ver os detalhes.', 'Detalhamento de Saída');
			const [companyId, outputId] = ids[0];
			await openOutputDetailModal(companyId, outputId);
		},
		async gerarPedidoFalta()
		{
			const ids = await this.getSelectedIds(['company_id', 'id']);
			if (ids.length !== 1)
				return notify('Selecione apenas uma saída para gerar o Pedido Falta.', 'Pedido Falta');
			const [companyId, outputId] = ids[0];

			// Carregar dados completos da saída para validar status e obter itens
			const output = await api.call(api.select_output, companyId, outputId);
			if (!output) return;

			if (output.status !== 60)
			{
				return notify(
					`A saída #${outputId} precisa estar com status Conferida (60) para gerar Pedido Falta.\n` +
					`Status atual: ${output.status}.`,
					'Pedido Falta'
				);
			}

			if (!output.order_id)
			{
				return notify(
					`A saída #${outputId} não está vinculada a um Pedido de Saída. Operação não disponível.`,
					'Pedido Falta'
				);
			}

			if (!hasShortage(output.items))
			{
				return notify(
					`A saída #${outputId} foi conferida sem faltas (todas as quantidades foram atendidas).\n` +
					`Pedido Falta não é necessário.`,
					'Pedido Falta'
				);
			}

		openShortageModal(
			companyId,
			parseInt(outputId, 10),
			output.order_id,
			output.items,
			() => this.loadPage()
		);
		},
		async verSolicitacoesUnpicking()
		{
			const ids = await this.getSelectedIds(['company_id', 'id']);
			if (ids.length !== 1)
				return notify('Selecione apenas uma saída para ver as solicitações de unpicking.', 'Unpicking');
			const [companyId, outputId] = ids[0];
			await openUnpickRequestsModal(companyId, outputId);
		},
		async verCaixasPicking()
		{
			const ids = await this.getSelectedIds(['company_id', 'id']);
			if (ids.length !== 1)
				return notify('Selecione apenas uma saída para ver as caixas de picking.', 'Caixas de Picking');
			const [companyId, outputId] = ids[0];
			await openPickingBoxesForOutputModal(companyId, outputId, () => this.loadPage());
		},
		async pickingSaida()
		{
			const ids = await this.getSelectedIds(['company_id', 'id']);
			if (ids.length !== 1)
				return notify('Selecione apenas uma saída para realizar a separação.', 'Picking');
			const [companyId, outputId] = ids[0];
			await openOutputPickingModal(companyId, outputId, () => this.loadPage());
		},
	});
	
	// Index = position in the backend's SELECT
	let fieldspecs = [
		{ index: 1, field: 'company_id', label: 'Empresa', types: [types.datalist()], placeholder: '', title: 'Empresa', store: 'companies', labels: ['nick'] },
		{ index: 2, field: 'id', label: 'Saída', types: [types.number(0, 2)], placeholder: '0', title: 'ID da saída física (coluna Saída na grade). Para filtrar várias saídas use este campo com Ou — não confundir com Pedido.' },
		{ index: 4, field: 'order_id', label: 'Pedido', types: [types.number(0, 2)], placeholder: '0', title: 'ID do pedido de saída (output_orders.id — coluna Pedido na grade). Valor numérico: 10545 ou 10.545 (vira 10545). Número externo do cliente vai em Referência.' },
		{ index: 15, field: 'program_id', label: 'Programa', types: [types.datalist()], placeholder: '', title: 'Programa da saída', store: 'programs', labels: ['name'] },
		{ index: 16, field: 'reference', label: 'Referência', types: [types.name()], placeholder: '', title: 'Referência do pedido' },
		{ index: 20, field: 'customer_id', label: 'Cliente', types: [types.datalist()], placeholder: 'Apelido ou CNPJ/CPF', title: 'Cliente do pedido de saída', store: 'customers', labels: ['nick'] },
		{ index: 22, field: 'route_id', label: 'Rota', types: [types.datalist()], placeholder: '', title: 'Rota de entrega do cliente', store: 'customer_routes', labels: ['code', 'name'] },
		{ index: 21, field: 'carrier_id', label: 'Transportador', types: [types.datalist()], placeholder: '', title: 'Transportador do pedido de saída', store: 'carriers', labels: ['nick'] },
		{ index: 35, field: 'delivery_sector', label: 'Setor Entrega', types: [], placeholder: '', title: 'Setor de entrega do pedido. Digite o nome ou um trecho.' },
		{ index: 3, field: 'order_type', label: 'Origem', types: [types.opt_order_type()], placeholder: '', title: 'Origem do pedido vinculado' },
		{ index: 5, field: 'created_on', label: 'Criação', types: [types.datetime()], placeholder: 'dd/mm/aaaa hh:mm:ss', title: 'Data e hora da criação da saída' },
		{ index: 6, field: 'expedited_on', label: 'Expedição', types: [types.datetime()], placeholder: 'dd/mm/aaaa hh:mm:ss', title: 'Data e hora de expedição da saída' },
		{ index: 27, field: 'picklist_printed_at', label: 'Picklist', types: [types.datetime()], placeholder: 'dd/mm/aaaa hh:mm:ss', title: 'Data/hora da última impressão do picklist (Pick Lists ou PickList Ordem). Vazio = ainda não impresso. No filtro: use É nulo / Não é nulo para não impresso / impresso.' },
		{ index: 7, field: 'warehouse_company_id', label: 'Remetente', types: [types.datalist()], placeholder: '', title: 'Empresa do armazém de coleta', store: 'companies', labels: ['nick'] },
		{ index: 8, field: 'warehouse_id', label: 'Armazém', types: [types.datalist()], placeholder: '', title: 'Armazém de coleta', store: 'warehouses', labels: ['name'] },
		{ index: 9, field: 'volume_type', label: 'Tipo Volume', types: [types.opt_volume_type(), types.datalist()], placeholder: '', title: 'Tipo de volume' },
		{ index: 10, field: 'volumes', label: 'Volumes', types: [types.number(0, 2)], placeholder: '0', title: 'Número de volumes' },
		{ index: 11, field: 'weight', label: 'Peso', types: [types.number(3)], placeholder: '0,000', title: 'Peso total dos volumes' },
		{ index: 12, field: 'total_cost', label: 'Custo Total', types: [types.number(2)], placeholder: '0,00', title: 'Custo total da saída' },
		{ index: 13, field: 'total_price', label: 'Preço Total', types: [types.number(2)], placeholder: '0,00', title: 'Preço total da saída' },
		{ index: 14, field: 'status', label: 'Status', types: [types.opt_output_status(), types.datalist()], placeholder: '', title: 'Status da saída' },
		{ index: 25, field: 'is_emergency', label: 'Emergencial', types: [types.opt_bool(), types.datalist()], placeholder: '', title: 'Saída de pedido emergencial (#MT-8EF7902)' },
		{ index: 18, field: 'lines_sku_summary', label: 'SKU (itens)', types: [], placeholder: 'trecho do SKU', title: 'Busca nos SKUs das linhas da saída (operador Contém recomendado)' },
		{ index: 19, field: 'lines_product_summary', label: 'Produto (itens)', types: [], placeholder: 'nome do produto', title: 'Busca nos nomes dos produtos das linhas (operador Contém recomendado)' },
	];

	let operations = [
		{ hash: '#ver-detalhes-saida',	label: 'Detalhes&nbsp;da&nbsp;Saída'	},
		{ hash: '#gerar-pedido-falta',	label: 'Gerar&nbsp;Pedido&nbsp;Falta'	},
		{ hash: '#ver-unpick-requests',	label: 'Solicitações&nbsp;de&nbsp;Unpicking' },
		{ hash: '#ver-caixas-picking',	label: 'Caixas&nbsp;de&nbsp;Picking'	},
		{ hash: '#picking-saida',		label: 'Picking'						},
		{ hash: '#cancel-outputs',		label: 'Cancelar&nbsp;Reservas'			},
		{ hash: '#create-invoices',		label: 'Gerar&nbsp;notas&nbsp;fiscais'	},
		{ hash: '#close-outputs',		label: 'Baixar&nbsp;Estoques'			},
		{ hash: '#send-to-dock-expedition',	label: 'Enviar&nbsp;para&nbsp;Doca&nbsp;Expedição' },
		{ hash: '#reverse-output',		label: 'Estornar&nbsp;Saída'			},
		{ hash: '#conferir-saida',		label: 'Conferir&nbsp;Saída'			},
	];

	let reports = [
		{ hash: '#export-list-csv',	label: 'Exportar&nbsp;listagem&nbsp;(CSV)'	},
		{ hash: '#export-items-csv',	label: 'Exportar&nbsp;itens&nbsp;(CSV)'	},
		{ hash: '#print-control', 	label: 'Controle&nbsp;de&nbsp;Picking'	},
		{ hash: '#print-pallets', 	label: 'Derrubada&nbsp;de&nbsp;Pallets'	},
		{ hash: '#print-picklists',	label: 'Pick&nbsp;Lists' 				},
		{ hash: '#print-picklist-order',	label: 'PickList&nbsp;-&nbsp;Ordem' },
		{ hash: '#print-ar',		label: 'Aviso&nbsp;de&nbsp;Remessa'	},
		{ hash: '#print-conference',	label: 'Conferência&nbsp;de&nbsp;Saída'	},
		{ hash: '#print-details',	label: 'Saída&nbsp;Detalhada'	},
		{ hash: '#print-balance-trace',	label: 'Rastreio&nbsp;de&nbsp;Saldo'	},
		{ hash: '#print-labels',	label: 'Etiquetas&nbsp;de&nbsp;Volumes'	},
		{ hash: '#print-stage-labels',	label: 'Etiqueta&nbsp;de&nbsp;Saída'	},
	];

	router.set('#cancel-outputs', 	outputs_crud.cancelOutputs.bind(outputs_crud));
	router.set('#create-invoices', 	outputs_crud.createInvoices.bind(outputs_crud));
	router.set('#close-outputs', 	outputs_crud.closeOutputs.bind(outputs_crud));
	router.set('#send-to-dock-expedition', outputs_crud.sendToDockExpedition.bind(outputs_crud));
	router.set('#reverse-output',	outputs_crud.reverseOutput.bind(outputs_crud));
	router.set('#conferir-saida',		outputs_crud.conferirSaida.bind(outputs_crud));
	router.set('#ver-detalhes-saida',	outputs_crud.verDetalhesSaida.bind(outputs_crud));
	router.set('#gerar-pedido-falta',	outputs_crud.gerarPedidoFalta.bind(outputs_crud));
	router.set('#ver-unpick-requests',	outputs_crud.verSolicitacoesUnpicking.bind(outputs_crud));
	router.set('#ver-caixas-picking',	outputs_crud.verCaixasPicking.bind(outputs_crud));
	router.set('#picking-saida',		outputs_crud.pickingSaida.bind(outputs_crud));

	router.set('#export-list-csv', outputs_crud.exportListCsv.bind(outputs_crud));
	router.set('#export-items-csv', outputs_crud.exportItemsCsv.bind(outputs_crud));
	router.set('#print-control', outputs_crud.printPickControl.bind(outputs_crud));
	router.set('#print-pallets', outputs_crud.printPalletsOverturn.bind(outputs_crud));
	router.set('#print-picklists', outputs_crud.printPickLists.bind(outputs_crud));
	router.set('#print-picklist-order', outputs_crud.printPickListOrder.bind(outputs_crud));
	router.set('#print-ar', outputs_crud.printAvisoRemessa.bind(outputs_crud));
	router.set('#print-conference', outputs_crud.printConference.bind(outputs_crud));
	router.set('#print-details', outputs_crud.printDetails.bind(outputs_crud));
	router.set('#print-balance-trace', outputs_crud.printBalanceTrace.bind(outputs_crud));
	router.set('#print-labels', outputs_crud.printLabels.bind(outputs_crud));
	router.set('#print-stage-labels', outputs_crud.printStageLabels.bind(outputs_crud));

	outputs_crud.run(
		'outputs',
		'Saídas',
		document.getElementById('workarea'),
		view,
		record_output_form,
		fieldspecs,
		operations,
		reports
	);

};

export default outputs;

